/**
 * The day panel stepping from one day to the next — the cards arriving, rather than the body
 * blinking.
 *
 * ── What this replaces, and why it is not the surface it replaced ─────────────────────────────
 * Until 9 September 2026 the step was one CSS keyframe on the panel's whole scroll body, replayed
 * by `shared/lib/useSwapReplay`: `opacity .2 -> 1` over a 6px rise, 320ms, everything at once. The
 * owner called it dramatic and asked for smooth and fast. Three things were wrong with it, and all
 * three are properties of animating the BODY rather than what is in it — five cards blinking in
 * lockstep, a start low enough to read as a wipe, and a length that outlives the gesture.
 *
 * So the panel stops sharing a mechanism with the WEEK's phase switch. That switch keeps
 * `useSwapReplay` and its CSS: it was tuned on 8 September to end exactly with the day columns'
 * `flex-grow` settle, it is not what the owner complained about, and re-timing it here would break
 * a pairing that was measured. One hook, two surfaces, one complaint — the complaint's surface
 * moves and the other does not.
 *
 * ── Why the DOM, and not `motion.li` ─────────────────────────────────────────────────────────
 * The things that must arrive are the list's cards, the warm-up run and the day's foot: a dozen
 * elements rendered by four components, two of them behind `MoveableBlock`'s drag wiring. Turning
 * each into a Motion component would thread an `initial` / `animate` pair through every one of
 * them for an effect none of them owns. Motion's imperative `animate` takes the elements as they
 * are, so the step stays one module and the components stay ignorant of it.
 *
 * ── `useLayoutEffect`, and it is load-bearing ────────────────────────────────────────────────
 * A plain effect runs AFTER paint: the new day would be drawn at full opacity for one frame and
 * then dropped back to 0.5 to be faded in — a flash, which is a worse version of the defect this
 * fixes. A layout effect runs after React has written the DOM and before the browser paints, so
 * the first frame the trainer sees is already the animation's first frame.
 *
 * ── Reduced motion is a PARAMETER ────────────────────────────────────────────────────────────
 * `tokens/base.css` collapses CSS durations and cannot reach a Web Animations API animation, which
 * is what Motion drives. So the preference arrives from `useScreenMotionPreference` and the step
 * simply does not run — the new day is painted, still, in one frame.
 */

import { animate } from 'motion/react';
import { useLayoutEffect, useRef, type RefObject } from 'react';

import { screenMotion } from '~/shared/design-system/motion';

/**
 * What arrives: every one of the body's own children except the exercise list, and then that
 * list's items one at a time. Structural rather than class-based on purpose — CSS Module class
 * names are hashed, so a selector written against one is a selector that silently matches nothing
 * after the stylesheet is edited. `querySelectorAll` answers in document order, so the warm-up run
 * leads and the cards follow it in the order they are read.
 */
const STEP_ITEMS = ':scope > :not(ol), :scope > ol > li';

/**
 * Where the cascade stops growing. Past it every remaining element starts together.
 *
 * A 22ms step over a fourteen-element day would put a third of a second between the warm-up and
 * the foot, and a tail that long stops reading as one gesture and starts reading as a list
 * assembling itself — which is the thing being fixed. Six positions is 110ms of spread, which is
 * enough for the eye to read a direction and short enough to still land inside the step.
 */
const CASCADE_CAP = 5;

/**
 * One end of a step, as a number Motion can tween from — or `null`, which is Motion's own "start
 * from whatever this element is showing" rather than a stand-in for a value.
 *
 * `screenMotion` types a step's two ends as an open bag whose values may also be a KEYFRAME LIST,
 * because one entry in that table is a whole life rather than an arrival. `dayStep` is not that
 * entry and never will be; a list arriving here would mean the table had changed under this file,
 * and starting from the element's own state is the honest thing to do about it.
 */
const frame = (value: number | number[] | undefined): number | null =>
  typeof value === 'number' ? value : null;

/**
 * @param body The panel's scroll body. Its children are what arrives.
 * @param daySlotToken The day on screen. A change to it is the step; nothing runs on arrival.
 * @param reducedMotion Whether the viewer asked for stillness. Required, so no caller forgets it.
 */
export function useDayStepMotion(
  body: RefObject<HTMLElement | null>,
  daySlotToken: string,
  reducedMotion: boolean,
): void {
  const shown = useRef(daySlotToken);

  useLayoutEffect(() => {
    if (shown.current === daySlotToken) return undefined;
    shown.current = daySlotToken;

    const element = body.current;
    if (reducedMotion || element === null) return undefined;

    const items = [...element.querySelectorAll<HTMLElement>(STEP_ITEMS)];
    if (items.length === 0) return undefined;

    const step = screenMotion('dayStep', { reducedMotion });

    /* ── The first frame has to BE the animation's first frame ────────────────────────────────
     * Motion resolves an imperative animation's keyframes inside its own batched frame loop,
     * which is one rAF away — so the browser paints the new day at rest first and the animation
     * takes over on the frame after. Measured on 9 September 2026: the first card stood at its
     * resting top, then moved 4px DOWN on the next frame before easing back up. A flash and a
     * bounce, which is a worse defect than the one being fixed.
     *
     * So the step's own start state is written onto the elements here, synchronously, inside the
     * layout effect and therefore before any paint. The numbers are still the table's and are read
     * from the same `step.initial` the keyframes below use — one source, applied at two moments. */
    for (const item of items) {
      item.style.opacity = String(frame(step.initial.opacity) ?? 1);
      item.style.transform = `translateY(${String(frame(step.initial.y) ?? 0)}px)`;
    }

    /* A missing end reads as `null` — see `frame` above. */
    const playback = animate(
      items,
      {
        opacity: [frame(step.initial.opacity), frame(step.animate.opacity)],
        y: [frame(step.initial.y), frame(step.animate.y)],
      },
      {
        duration: step.transition.duration,
        ease: [...step.transition.ease],
        delay: (index: number) =>
          screenMotion('dayStep', { reducedMotion, index: Math.min(index, CASCADE_CAP) }).transition
            .delay,
      },
    );

    /* Motion leaves the last keyframe on the element as an inline style, and a leftover
     * `transform` — even `translateY(0px)` — makes every card a containing block for anything
     * `position: fixed` inside it. So the two properties it wrote are taken back off, both when
     * the step finishes and when a faster trainer starts the next one over the top of it. */
    const settle = () => {
      for (const item of items) {
        item.style.removeProperty('opacity');
        item.style.removeProperty('transform');
      }
    };
    void playback.then(settle);

    return () => {
      playback.stop();
      settle();
    };
  }, [body, daySlotToken, reducedMotion]);
}
